// React keeps a direct reference to every DOM node it created and assumes nothing else moves
// them. Chrome's built-in page translation and many extensions (password managers, overlay
// tools) do exactly that: they replace or reparent nodes inside the tree React owns. React
// then calls parent.removeChild() on a parent that no longer owns the node, the DOM throws
// "The node to be removed is not a child of this node", and the whole app unmounts.
//
// Downgrading those two calls to a no-op keeps the page alive at the cost of leaving the
// stray node where the external script put it. See facebook/react#11538.
export function installDomGuard(): void {
  const nativeRemoveChild = Node.prototype.removeChild;
  const nativeInsertBefore = Node.prototype.insertBefore;

  Node.prototype.removeChild = function guardedRemoveChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      console.warn("Skipped removeChild: the node was moved by something outside React", child);
      return child;
    }
    return nativeRemoveChild.call(this, child) as T;
  };

  Node.prototype.insertBefore = function guardedInsertBefore<T extends Node>(this: Node, node: T, reference: Node | null): T {
    if (reference && reference.parentNode !== this) {
      console.warn("Appended instead of insertBefore: the reference node was moved by something outside React", node);
      return this.appendChild(node);
    }
    return nativeInsertBefore.call(this, node, reference) as T;
  };
}
